import { HttpErrorResponse } from '@angular/common/http';
import type { EmailFailureKind, EmailSendApiResponse } from './email-send.types';

/** User-facing copy (never include raw Postmark or API JSON). */
export const EMAIL_INACTIVE_USER_MESSAGE =
  "We couldn't send a notification email because the recipient address is inactive.";
export const EMAIL_FALLBACK_USER_MESSAGE =
  'Notification email could not be sent. Please verify the email address.';

function parseBodyObject(raw: unknown): Record<string, unknown> | null {
  if (raw != null && typeof raw === 'object' && !Array.isArray(raw)) {
    return raw as Record<string, unknown>;
  }
  if (typeof raw === 'string' && raw.trim().startsWith('{')) {
    try {
      const o = JSON.parse(raw) as unknown;
      if (o != null && typeof o === 'object' && !Array.isArray(o)) {
        return o as Record<string, unknown>;
      }
    } catch {
      return null;
    }
  }
  return null;
}

function messageLooksInactive(msg: string): boolean {
  return msg.toLowerCase().includes('inactive');
}

/**
 * Classifies Postmark-style or proxy error payloads without surfacing raw JSON to the UI.
 */
export function classifyEmailSendFailure(error: unknown): EmailFailureKind {
  if (!(error instanceof HttpErrorResponse)) {
    return 'other';
  }
  const parsed = parseBodyObject(error.error);
  if (parsed) {
    const code = parsed['ErrorCode'] ?? parsed['errorCode'];
    const msg = String(parsed['Message'] ?? parsed['message'] ?? '');
    if (code === 406 || code === '406') {
      return 'inactive';
    }
    if (messageLooksInactive(msg)) {
      return 'inactive';
    }
  }
  if (typeof error.error === 'string' && messageLooksInactive(error.error)) {
    return 'inactive';
  }
  return 'other';
}

/** Log full technical detail (console / optional forwarding to logging backend). */
export function logEmailSendHttpFailure(error: unknown, label: string): void {
  if (error instanceof HttpErrorResponse) {
    console.error(`[${label}] /email/send failed`, {
      status: error.status,
      statusText: error.statusText,
      url: error.url,
      error: error.error,
    });
  } else {
    console.error(`[${label}] /email/send failed`, error);
  }
}

/**
 * Turns a failed email HTTP call into the same success-shaped stream result used for normal
 * Postmark failures, so submission flows never see HttpErrorResponse from this endpoint.
 */
export function buildSyntheticEmailSendResponse(error: unknown): EmailSendApiResponse {
  const kind = classifyEmailSendFailure(error);
  return {
    success: true,
    emailSent: false,
    emailFailureKind: kind,
    emailWarning: kind === 'inactive' ? EMAIL_INACTIVE_USER_MESSAGE : EMAIL_FALLBACK_USER_MESSAGE,
  };
}
